/**
 * Phase 1 keyword-picker — shared typeahead used by Brief create, Tracking
 * add, and (eventually) any surface that asks for a keyword.
 *
 * Forked from the inline KeywordTypeahead in brief-create-button.tsx to
 * preserve the exact keyboard / mouse / a11y behavior, but sources data from
 * the new /v1/projects/:projectId/keywords/search endpoint (cross-run) and
 * carries richer metadata (volume / difficulty / intent) into the caller.
 *
 * Free-text fallback is always allowed unless the caller disables it. If the
 * user picks a real keyword row, `onChange` fires with `kind: 'picked'` +
 * IDs; otherwise `kind: 'free-text'`.
 */
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import type { Intent, KeywordSearchResult } from '@seo/types';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/cn';
import {
  useKeywordSearch,
  type KeywordSearchFilters,
} from '@/features/keywords/use-keyword-search';

export type KeywordPickerValue =
  | {
      kind: 'picked';
      keywordId: string;
      keywordRunId: string;
      term: string;
      volume: number | null;
      difficulty: number | null;
      intent: Intent | null;
      clusterLabel: string | null;
    }
  | { kind: 'free-text'; term: string };

export interface KeywordPickerProps {
  /** Required — scopes search. When undefined, the picker renders disabled. */
  projectId: string | undefined;
  /** Current selection (controlled). */
  value: KeywordPickerValue | null;
  onChange: (v: KeywordPickerValue) => void;
  placeholder?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  /** Default true. When false, only picked rows are accepted. */
  allowFreeText?: boolean;
  /** Optional filters passed through to search. */
  clusterId?: string;
  intent?: Intent;
  /** Show volume / difficulty / intent chips inline on each row. Default true. */
  showMetrics?: boolean;
  /** For accessibility / label wiring. */
  ariaLabel?: string;
}

// 250ms debounce is a good balance for typeahead — mirrors Ahrefs / SEMrush.
const SEARCH_DEBOUNCE_MS = 250;

export function KeywordPicker({
  projectId,
  value,
  onChange,
  placeholder,
  autoFocus,
  disabled,
  allowFreeText = true,
  clusterId,
  intent,
  showMetrics = true,
  ariaLabel,
}: KeywordPickerProps): JSX.Element {
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [rawInput, setRawInput] = useState(value?.term ?? '');
  const [debounced, setDebounced] = useState(rawInput);

  // Sync input if caller pushes a new value (e.g. defaultKeyword prop).
  useEffect(() => {
    setRawInput(value?.term ?? '');
  }, [value?.term]);

  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(rawInput), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [rawInput]);

  const filters: KeywordSearchFilters = useMemo(
    () => ({
      ...(clusterId ? { clusterId } : {}),
      ...(intent ? { intent } : {}),
    }),
    [clusterId, intent],
  );

  const search = useKeywordSearch(projectId, debounced, filters);

  const query = rawInput.trim();
  const results = search.data?.items ?? [];
  const exactMatch = results.find(
    (r) => r.term.trim().toLowerCase() === query.toLowerCase(),
  );
  const canCreate = allowFreeText && query.length >= 1 && !exactMatch;

  type Row =
    | { kind: 'option'; id: string; keyword: KeywordSearchResult }
    | { kind: 'create'; id: '__create__'; term: string };

  const rows = useMemo((): Row[] => {
    const items: Row[] = results.map((r) => ({ kind: 'option', id: r.id, keyword: r }));
    if (canCreate) items.push({ kind: 'create', id: '__create__', term: query });
    return items;
  }, [results, canCreate, query]);

  useEffect(() => {
    setHighlight(0);
  }, [debounced]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const pickRow = (row: Row) => {
    if (row.kind === 'option') {
      const k = row.keyword;
      onChange({
        kind: 'picked',
        keywordId: k.id,
        keywordRunId: k.keyword_run_id,
        term: k.term,
        volume: k.volume,
        difficulty: k.difficulty,
        intent: k.intent,
        clusterLabel: k.cluster_label,
      });
      setRawInput(k.term);
    } else {
      onChange({ kind: 'free-text', term: row.term });
      setRawInput(row.term);
    }
    setOpen(false);
  };

  const handleTextChange = (next: string) => {
    setRawInput(next);
    setOpen(next.trim().length > 0);
    // While the user is typing, treat it as free text until a row is chosen.
    // Only emit if it actually differs from what's currently held so we don't
    // clobber a picked selection on focus.
    if (value?.term !== next) {
      onChange({ kind: 'free-text', term: next });
    }
  };

  const showMenu =
    open && !disabled && Boolean(projectId) && (search.isFetching || rows.length > 0 || query.length >= 1);

  return (
    <div ref={rootRef} className="relative">
      <Input
        value={rawInput}
        disabled={disabled || !projectId}
        autoFocus={autoFocus}
        autoComplete="off"
        role="combobox"
        aria-expanded={showMenu}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-label={ariaLabel}
        aria-activedescendant={
          showMenu && rows[highlight] ? `${listId}-${rows[highlight].id}` : undefined
        }
        placeholder={placeholder ?? 'Search or type a keyword…'}
        onFocus={() => {
          if (rawInput.trim().length > 0) setOpen(true);
        }}
        onChange={(e) => handleTextChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            setOpen(false);
            return;
          }
          if (!showMenu || rows.length === 0) return;
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlight((i) => (i + 1) % rows.length);
            return;
          }
          if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlight((i) => (i - 1 + rows.length) % rows.length);
            return;
          }
          if (e.key === 'Enter') {
            const row = rows[highlight];
            if (row) {
              e.preventDefault();
              pickRow(row);
            }
          }
        }}
      />

      {showMenu ? (
        <ul
          id={listId}
          role="listbox"
          className="absolute left-0 right-0 top-[calc(100%+4px)] z-[60] max-h-72 overflow-y-auto rounded-md border bg-card py-1 text-card-foreground shadow-md"
        >
          {search.isFetching && rows.length === 0 ? (
            <li className="px-3 py-2 text-xs text-muted-foreground">Searching…</li>
          ) : null}

          {!search.isFetching && rows.length === 0 && query.length >= 1 ? (
            <li className="px-3 py-2 text-xs text-muted-foreground">
              No matches. {allowFreeText ? 'Keep typing to use your own keyword.' : 'Try a different search.'}
            </li>
          ) : null}

          {rows.map((row, index) => {
            const active = index === highlight;
            const isSelected =
              row.kind === 'option' &&
              value?.kind === 'picked' &&
              value.keywordId === row.keyword.id;
            return (
              <li
                key={row.id}
                id={`${listId}-${row.id}`}
                role="option"
                aria-selected={active}
              >
                <button
                  type="button"
                  className={cn(
                    'flex w-full items-center gap-2 px-3 py-2 text-left text-sm',
                    active ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/70',
                  )}
                  onMouseEnter={() => setHighlight(index)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pickRow(row)}
                >
                  {row.kind === 'option' ? (
                    <>
                      <Check
                        className={cn(
                          'h-3.5 w-3.5 shrink-0',
                          isSelected ? 'opacity-100' : 'opacity-0',
                        )}
                      />
                      <span className="min-w-0 flex-1 truncate">{row.keyword.term}</span>
                      {showMetrics ? <MetricsChips k={row.keyword} /> : null}
                    </>
                  ) : (
                    <>
                      <span className="w-3.5 shrink-0" />
                      <span className="min-w-0 flex-1 truncate">
                        <span className="text-muted-foreground">Use </span>
                        <span className="font-medium">"{row.term}"</span>
                      </span>
                      <span className="shrink-0 text-[11px] text-muted-foreground">New</span>
                    </>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

/**
 * Compact inline metadata for each option: volume, difficulty, intent chip.
 * All optional — anything null is omitted.
 */
function MetricsChips({ k }: { k: KeywordSearchResult }): JSX.Element {
  return (
    <span className="flex shrink-0 items-center gap-1.5 text-[11px] text-muted-foreground">
      {k.volume != null ? (
        <span title="Monthly searches">V:{formatVolume(k.volume)}</span>
      ) : null}
      {k.difficulty != null ? (
        <span title="Ranking difficulty (0-100)">D:{k.difficulty}</span>
      ) : null}
      {k.intent ? <span className="uppercase" title="Search intent">{k.intent.slice(0, 4)}</span> : null}
      {k.cluster_label ? (
        <span className="max-w-[100px] truncate" title={`Cluster: ${k.cluster_label}`}>
          #{k.cluster_label}
        </span>
      ) : null}
    </span>
  );
}

function formatVolume(v: number): string {
  if (v >= 1000) return `${(v / 1000).toFixed(v >= 10_000 ? 0 : 1)}k`;
  return String(v);
}
